import _, { eq } from "lodash";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import ReactDatePicker from "react-datepicker";
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Form,
  FormProps,
  Header,
  Input,
  Label,
  Message,
  Modal,
  Segment,
  Select,
  Table,
  TextArea,
} from "semantic-ui-react";
import { dialogControl } from "../controls/dialog-control";
import { notificationControl } from "../controls/notification-control";
import { useCurrentUser } from "../hooks/auth-hook";
import {
  ModalControlData,
  useModalControl,
  useRequest,
} from "../hooks/commonHooks";
import { trimSetField, useFormConnect, useFormData } from "../hooks/formHooks";
import { formatEnumName, hostUrl, httpRequest, restRequest } from "../http";
import {
  AREAS,
  ENUM_TYPES_EQUIPMENT_BRAND,
  ENUM_TYPES_EQUIPMENT_DEVICE,
  ENUM_TYPES_EQUIPMENT_LOCATION,
  ENUM_TYPES_EQUIPMENT_MODEL,
  ENUM_TYPES_EQUIPMENT_PART,
  ENUM_TYPES_EQUIPMENT_PRODUCT,
  ENUM_TYPES_EQUIPMENT_PROJECT_USED_FOR,
  ENUM_TYPES_EQUIPMENT_STORAGE_PLACE,
  ENUM_TYPES_EQUIPMENT_TEAM,
  ENUM_TYPES_EQUIPMENT_TYPE,
  FileContainerType,
  JournalizationDetailField,
  LANG,
  Override,
  Translate,
} from "../models/common_model";
import { PageData } from "../models/common_model";
import {
  EquipmentCalibrationData,
  EquipmentChangeData,
  EquipmentConfig,
  EquipmentData,
} from "../models/equipment_model";
import {
  fieldError,
  formatDate,
  formatDateTime,
  parseDateTime,
  parseQueryObject,
} from "../utils";
import { Comments } from "./commons/Comments";
import { CommonEnumSelector, EnumSearchDropdown } from "./commons/CommonEnum";
import {
  JournalizationComponent,
  JournalizationDetailContent,
} from "./commons/JournalizationComponent";
import { Page } from "./commons/Page";
import {
  ProductionModelDropdown,
  ProductionModelSearchDropdown,
} from "./commons/ProductionModelComponent";
import { EquipmentCalibrationComponent } from "./EquipmentCalibrationComponent";
import { EquipmentComponentPrintQR } from "./EquipmentComponentPrintQR";
import { TextEditor } from "./commons/TextEditor";
import { LewittProjectDropdown } from "./commons/LewittProject";
import { LewittRemoteSearchDropdown } from "./commons/LewittDropdown";
import { FilesInput } from "./commons/FilesInput";
import { T } from "./commons/Local";
import { LanguageContext } from "../context";

export function EquipmentComponent(props: {}) {
  const queryParameters = parseQueryObject();
  const currentUser = useCurrentUser();
  const [searchData, setSearchField] = useFormData<
    Override<EquipmentData, {
      need_calibration: number;
      calibration_date_start: string;
      calibration_date_end: string;
      next_calibration_date_start: string;
      next_calibration_date_end: string;
      by_bundles: boolean;
      need_confirm: boolean;
      need_calibrate_asap: boolean;
      responsible_person: string;
      product: string;
      history: string;
    }>
  >((queryParameters?.testing_station_id ? {id: parseInt(queryParameters.testing_station_id), by_bundles: true} : {}) as any);

  const [equipments, setSearchData, searchLoading] = useFormConnect<
    any,
    PageData<EquipmentData>
  >(searchData, "equipments/search", "post");

  
  const [sort, setSort] = useState({ name: "id", dir: 1 });

  useEffect(() => {
    const sortInfo: any = {};
    if (sort.name && sort.dir) {
      sortInfo.sorted_field = sort.name;
      sortInfo.sorted_direction = sort.dir;
    } else {
      sortInfo.sorted_field = null;
      sortInfo.sorted_direction = null;
    }

    setSearchData((sd: any) => ({ ...sd, ...sortInfo }));
  }, [sort]);

  const editModalControl = useModalControl();
  const confirmModalControl = useModalControl<[number, number, number]>([0, 0, 0]); // [ID, openCount to refresh]
  const calibrationModalControl = useModalControl();

  const [currentEditId, setCurrentEditId] = useState<number>();
  const [currentCopyEditId, setCurrentCopyEditId] = useState<number>();
  const [currentCalibrationEquipmentId, setCurrentCalibrationEquipmentId] =
    useState<number>();

  const [config] = useRequest<EquipmentConfig>("equipments/config");

  const journalizationModalControl = useModalControl();
  const [currentJournalizationId, setCurrentJournalizationId] =
    useState<number>();

  const printQrModalControl = useModalControl<any>();
  const importModalControl = useModalControl<any>();

  const [t] = useContext(LanguageContext);

  const isNeedCalibration = searchData.need_calibration === 1;

  return (
    <>
      <Segment.Group>
        <Segment attached="top" loading={searchLoading}>
          <Form
            onSubmit={(e) =>
              setSearchData((d: any) => ({
                ...d,
                ...searchData,
                refresh_number: (d.refresh_number || 0) + 1,
                page_number: 1,
              }))
            }
          >
            <Form.Group>
              <Form.Field inline>
                <Input
                  placeholder="#ID"
                  name="id"
                  value={searchData["id"] || ""}
                  onChange={setSearchField} onBlur={trimSetField(setSearchField)}
                  size="mini"
                />
              </Form.Field>

              <Form.Field inline>
                <EnumSearchDropdown
                  typeId={ENUM_TYPES_EQUIPMENT_TEAM}
                  placeholder={t("Team")}
                  name="team"
                  value={searchData["team"] || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Field inline>
                <EnumSearchDropdown
                  typeId={ENUM_TYPES_EQUIPMENT_TYPE}
                  placeholder={t("Type")}
                  name="type"
                  value={searchData["type"] || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Field inline>
                <EnumSearchDropdown
                  typeId={ENUM_TYPES_EQUIPMENT_DEVICE}
                  placeholder={t("Product Category")}
                  name="device"
                  value={searchData["device"] || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Field inline>
                <EnumSearchDropdown
                  typeId={ENUM_TYPES_EQUIPMENT_BRAND}
                  placeholder={t("Brand")}
                  name="brand"
                  value={searchData["brand"] || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Field inline>
                <EnumSearchDropdown
                  typeId={ENUM_TYPES_EQUIPMENT_MODEL}
                  placeholder={t("Model")}
                  name="model"
                  value={searchData["model"] || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Field inline>
                <EnumSearchDropdown
                  typeId={ENUM_TYPES_EQUIPMENT_PART}
                  placeholder={t("Part")}
                  name="part"
                  value={searchData["part"] || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Field inline>
                <EnumSearchDropdown
                  typeId={ENUM_TYPES_EQUIPMENT_LOCATION}
                  placeholder={t("Location")}
                  name="location"
                  value={searchData["location"] || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Field inline>
                <EnumSearchDropdown
                  typeId={ENUM_TYPES_EQUIPMENT_STORAGE_PLACE}
                  placeholder={t("Storage Place")}
                  name="storage_place"
                  value={searchData["storage_place"] || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Field inline>
                <Input
                  placeholder={t("Serial, Engraved or PO Number")}
                  name="number"
                  value={searchData["number"] || ""}
                  onChange={setSearchField} onBlur={trimSetField(setSearchField)}
                  size="mini"
                  style={{ width: '12rem' }}
                />
              </Form.Field>
            </Form.Group>

            <Form.Group inline>
              <Form.Field inline>
                 <EnumSearchDropdown
                  typeId={ENUM_TYPES_EQUIPMENT_PROJECT_USED_FOR}
                  placeholder={t("Project Used For")}
                  name="product"
                  value={searchData["product"] || ""}
                  onChange={setSearchField}
                />
              </Form.Field>

              <Form.Field inline>
                <Input
                  placeholder={t("Owner")}
                  name="owner"
                  value={searchData["owner"] || ""}
                  onChange={setSearchField} onBlur={trimSetField(setSearchField)}
                  size="mini"
                />
              </Form.Field>

              <Form.Field inline>
                <Input
                  placeholder={t("Budget Code")}
                  name="budget_code"
                  value={searchData["budget_code"] || ""}
                  onChange={setSearchField} onBlur={trimSetField(setSearchField)}
                  size="mini"
                />
              </Form.Field>

              <Form.Field inline>
                <Input
                  placeholder={t("Lewitt Inventory Number")}
                  name="lewitt_inventory_number"
                  value={searchData["lewitt_inventory_number"] || ""}
                  onChange={setSearchField} onBlur={trimSetField(setSearchField)}
                  size="mini"
                  style={{ width: "12rem" }}
                />
              </Form.Field>

              <Form.Input inline size="mini">
                <Dropdown
                  name="current_state"
                  value={searchData.current_state || -1}
                  onChange={setSearchField}
                  options={[
                    { key: -1, value: -1, text: t(["Current State", ": ", "All"], [1]) },
                    ...((config && config.EQUIPMENT_CURRENT_STATES) || []).map(
                      (v) => ({ key: v[0], text: v[1], value: v[0] })
                    ),
                  ]}
                  search
                  selection
                ></Dropdown>
              </Form.Input>
              <Form.Field inline>
                <Input
                  placeholder={t("Comment")}
                  name="remark"
                  value={searchData["remark"] || ""}
                  onChange={setSearchField}
                  size="mini"
                  style={{ width: '15rem' }}
                />
              </Form.Field>
              <Form.Field inline>
                <Input
                  placeholder={t("History")}
                  name="history"
                  value={searchData["history"] || ""}
                  onChange={setSearchField}
                  size="mini"
                  style={{ width: '15rem' }}
                />
              </Form.Field>
              <Form.Input inline label={t("By Bundles")}>
                <Checkbox checked={searchData.by_bundles} onClick={(e, d) => setSearchField(null, { name: 'by_bundles', value: d.checked || false })}></Checkbox>
              </Form.Input>

              {searchData.by_bundles && <Form.Input inline size="mini">
                <Dropdown
                  name="bundle_status"
                  value={searchData.bundle_status || -1}
                  onChange={setSearchField}
                  options={[
                    { key: -1, value: -1, text: t(["Bundle Status", ": ", "All"], [1]) },
                    ...((config && config.EQUIPMENT_BUNDLE_STATUS) || []).map(
                      (v) => ({ key: v[0], text: v[1], value: v[0] })
                    ),
                  ]}
                  search
                  selection
                ></Dropdown>
              </Form.Input>}

              <Form.Input inline label={t("Need Confirm")}>
                <Checkbox checked={searchData.need_confirm} onClick={(e, d) => setSearchField(null, { name: 'need_confirm', value: d.checked || false })}></Checkbox>
              </Form.Input>
            </Form.Group>
            <Form.Group>
              <Form.Input inline size="mini">
                <Dropdown
                  name="need_calibration"
                  value={searchData.need_calibration || -1}
                  onChange={setSearchField}
                  options={[
                    { key: -1, text: t(["Need Calibration", ": ", "All"], [1]), value: -1 },
                    { key: 1, text: "Yes", value: 1 },
                    { key: 2, text: "No", value: 2 },
                  ]}
                  search
                  selection
                ></Dropdown>
              </Form.Input>

              <Form.Input
                inline
                className="date"
                label={t("Calibration Date")}
                size="mini"
              >
                <table cellPadding={0} cellSpacing={0}>
                  <tr>
                    <td>
                      <ReactDatePicker
                        showTimeSelect
                        placeholderText={t("From")}
                        dateFormat="yyyy-MM-d HH:mm"
                        timeFormat="HH:mm"
                        selected={parseDateTime(
                          searchData.calibration_date_start,
                          true
                        )}
                        onChange={(value: Date) =>
                          setSearchField(null, {
                            name: "calibration_date_start",
                            value: formatDateTime(value, true),
                          })
                        }
                      ></ReactDatePicker>
                    </td>
                    <th style={{ padding: "0 3px" }}> - </th>
                    <td>
                      <ReactDatePicker
                        showTimeSelect
                        placeholderText={t("To")}
                        dateFormat="yyyy-MM-d HH:mm"
                        timeFormat="HH:mm"
                        minDate={parseDateTime(
                          searchData.calibration_date_start,
                          true
                        )}
                        selected={parseDateTime(
                          searchData.calibration_date_end,
                          true
                        )}
                        onChange={(value: Date) =>
                          setSearchField(null, {
                            name: "calibration_date_end",
                            value: formatDateTime(value, true),
                          })
                        }
                      ></ReactDatePicker>
                    </td>
                  </tr>
                </table>
              </Form.Input>

              <Form.Input
                inline
                className="date"
                label={t("Next Calibration")}
                size="mini"
              >
                <table cellPadding={0} cellSpacing={0}>
                  <tr>
                    <td>
                      <ReactDatePicker
                        showTimeSelect
                        placeholderText={t("From")}
                        dateFormat="yyyy-MM-d HH:mm"
                        timeFormat="HH:mm"
                        selected={parseDateTime(
                          searchData.next_calibration_date_start,
                          true
                        )}
                        onChange={(value: Date) =>
                          setSearchField(null, {
                            name: "next_calibration_date_start",
                            value: formatDateTime(value, true),
                          })
                        }
                      ></ReactDatePicker>
                    </td>
                    <th style={{ padding: "0 3px" }}> - </th>
                    <td>
                      <ReactDatePicker
                        showTimeSelect
                        placeholderText={t("To")}
                        dateFormat="yyyy-MM-d HH:mm"
                        timeFormat="HH:mm"
                        minDate={parseDateTime(
                          searchData.next_calibration_date_start,
                          true
                        )}
                        selected={parseDateTime(
                          searchData.next_calibration_date_end,
                          true
                        )}
                        onChange={(value: Date) =>
                          setSearchField(null, {
                            name: "next_calibration_date_end",
                            value: formatDateTime(value, true),
                          })
                        }
                      ></ReactDatePicker>
                    </td>
                  </tr>
                </table>
              </Form.Input>
              <Form.Input inline label={t("Need Calibrate ASAP")}>
                <Checkbox checked={searchData.need_calibrate_asap} onClick={(e, d) => setSearchField(null, { name: 'need_calibrate_asap', value: d.checked || false })}></Checkbox>
              </Form.Input>
            </Form.Group>

            <Form.Group inline>
              <Form.Field inline>
                <Input
                  placeholder={t("Reason For Missing Calibration")}
                  name="cause_of_no_calibration"
                  value={searchData.cause_of_no_calibration || ""}
                  onChange={setSearchField}
                  size="mini"
                  style={{ width: '15rem' }}
                />
              </Form.Field>

              <Form.Input inline>
                <LewittRemoteSearchDropdown fluid selection name="responsible_person" value={searchData.responsible_person || ''} onChange={setSearchField} url="users/email_dropdown_options"
                  allowAdditions
                  emptyText={t(["Responsible Person For Calibration", ": ", "All"], [1])}
                  className="compact mini"
                  style={{ width: '20rem' }} />
              </Form.Input>

              <div>
                <Button icon="search" type="submit" size="mini"></Button>
                <Button
                  as="a"
                  icon="file excel outline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(
                      hostUrl(
                        "equipments/export?search=" +
                        encodeURI(JSON.stringify(searchData))
                      ),
                      "_blank"
                    );
                  }}
                  size="mini"
                ></Button>

                <Button
                  icon="print"
                  as="a"
                  onClick={(e) => {
                    e.preventDefault();
                    printQrModalControl.data = { ...searchData };
                    printQrModalControl.setOpen(true);
                  }}
                  size="mini"
                  style={{ marginLeft: "1rem" }}
                ></Button>

                {/* {currentUser.admin && (
                  <Button
                    as="a"
                    onClick={(e) => {
                      e.preventDefault();

                      importModalControl.setOpen(true);
                    }}
                    size="mini"
                  >
                    Import
                  </Button>
                )} */}
              </div>
            </Form.Group>
          </Form>
        </Segment>
        <Segment attached="bottom" style={{ overflow: "auto" }}>
          <Table striped sortable selectable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell
                  sorted={
                    sort.name === "id"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("id" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "id", dir });
                    } else {
                      setSort({ name: "id", dir: 1 });
                    }
                  }}
                >
                  #ID
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "team"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("team" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "team", dir });
                    } else {
                      setSort({ name: "team", dir: 1 });
                    }
                  }}
                >
                  {t('Team')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "type"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("type" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "type", dir });
                    } else {
                      setSort({ name: "type", dir: 1 });
                    }
                  }}
                >
                  {t('Type')}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "device"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("device" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "device", dir });
                    } else {
                      setSort({ name: "device", dir: 1 });
                    }
                  }}
                >
                  {t("Product Category")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "brand"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("brand" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "brand", dir });
                    } else {
                      setSort({ name: "brand", dir: 1 });
                    }
                  }}
                >
                  {t("Brand")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "model"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("model" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "model", dir });
                    } else {
                      setSort({ name: "model", dir: 1 });
                    }
                  }}
                >
                  {t("Model")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "part"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("part" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "part", dir });
                    } else {
                      setSort({ name: "part", dir: 1 });
                    }
                  }}
                >
                  {t("Part")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "number"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("number" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "number", dir });
                    } else {
                      setSort({ name: "number", dir: 1 });
                    }
                  }}
                >
                  {t("Serial Number")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "engraved_number"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("engraved_number" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "engraved_number", dir });
                    } else {
                      setSort({ name: "engraved_number", dir: 1 });
                    }
                  }}
                >
                  {t("Engraved Serial")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "po_number"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("po_number" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "po_number", dir });
                    } else {
                      setSort({ name: "po_number", dir: 1 });
                    }
                  }}
                >
                  {t("PO Number")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "location"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("location" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "location", dir });
                    } else {
                      setSort({ name: "location", dir: 1 });
                    }
                  }}
                >
                  {t("Location")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "storage_place"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("storage_place" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "storage_place", dir });
                    } else {
                      setSort({ name: "storage_place", dir: 1 });
                    }
                  }}
                >
                  {t("Storage Place")}
                </Table.HeaderCell>
                {searchData.by_bundles && <Table.HeaderCell
                  sorted={
                    sort.name === "bundle_status"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("bundle_status" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "bundle_status", dir });
                    } else {
                      setSort({ name: "bundle_status", dir: 1 });
                    }
                  }}
                >
                  {t("Bundle Status")}
                </Table.HeaderCell>}
                <Table.HeaderCell
                  sorted={
                    sort.name === "current_state"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("current_state" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "current_state", dir });
                    } else {
                      setSort({ name: "current_state", dir: 1 });
                    }
                  }}
                >
                  {t("Current State")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "product"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("product" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "product", dir });
                    } else {
                      setSort({ name: "product", dir: 1 });
                    }
                  }}
                >
                  {t("Project Used For")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "owner"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("owner" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "owner", dir });
                    } else {
                      setSort({ name: "owner", dir: 1 });
                    }
                  }}
                >
                  {t("Owner")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "lewitt_inventory_number"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("lewitt_inventory_number" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "lewitt_inventory_number", dir });
                    } else {
                      setSort({ name: "lewitt_inventory_number", dir: 1 });
                    }
                  }}
                >
                  {t("Lewitt Inventory Number")}
                </Table.HeaderCell>
                <Table.HeaderCell
                  sorted={
                    sort.name === "last_modified_at"
                      ? ([undefined, "ascending", "descending"][
                        sort.dir
                      ] as any)
                      : undefined
                  }
                  onClick={() => {
                    if ("last_modified_at" === sort.name) {
                      const dir = (sort.dir + 1) % 3;
                      setSort({ name: "last_modified_at", dir });
                    } else {
                      setSort({ name: "last_modified_at", dir: 1 });
                    }
                  }}
                >
                  {t("Last Changed At")}
                </Table.HeaderCell>
                {isNeedCalibration && <>
                  <Table.HeaderCell
                    sorted={
                      sort.name === "calibration_date"
                        ? ([undefined, "ascending", "descending"][
                          sort.dir
                        ] as any)
                        : undefined
                    }
                    onClick={() => {
                      if ("calibration_date" === sort.name) {
                        const dir = (sort.dir + 1) % 3;
                        setSort({ name: "calibration_date", dir });
                      } else {
                        setSort({ name: "calibration_date", dir: 1 });
                      }
                    }}
                  >
                    {t("Calibration Date")}
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={
                      sort.name === "next_calibration_date"
                        ? ([undefined, "ascending", "descending"][
                          sort.dir
                        ] as any)
                        : undefined
                    }
                    onClick={() => {
                      if ("next_calibration_date" === sort.name) {
                        const dir = (sort.dir + 1) % 3;
                        setSort({ name: "next_calibration_date", dir });
                      } else {
                        setSort({ name: "next_calibration_date", dir: 1 });
                      }
                    }}
                  >
                    {t("Next Calibration")}
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={
                      sort.name === "cause_of_no_calibration"
                        ? ([undefined, "ascending", "descending"][
                          sort.dir
                        ] as any)
                        : undefined
                    }
                    onClick={() => {
                      if ("cause_of_no_calibration" === sort.name) {
                        const dir = (sort.dir + 1) % 3;
                        setSort({ name: "cause_of_no_calibration", dir });
                      } else {
                        setSort({ name: "cause_of_no_calibration", dir: 1 });
                      }
                    }}
                  >
                    {t("Reason For Missing Calibration")}
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={
                      sort.name === "responsible_person"
                        ? ([undefined, "ascending", "descending"][
                          sort.dir
                        ] as any)
                        : undefined
                    }
                    onClick={() => {
                      if ("responsible_person" === sort.name) {
                        const dir = (sort.dir + 1) % 3;
                        setSort({ name: "responsible_person", dir });
                      } else {
                        setSort({ name: "responsible_person", dir: 1 });
                      }
                    }}
                  >
                    {t("Responsible Person For Calibration")}
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={
                      sort.name === "reminder"
                        ? ([undefined, "ascending", "descending"][
                          sort.dir
                        ] as any)
                        : undefined
                    }
                    onClick={() => {
                      if ("reminder" === sort.name) {
                        const dir = (sort.dir + 1) % 3;
                        setSort({ name: "reminder", dir });
                      } else {
                        setSort({ name: "reminder", dir: 1 });
                      }
                    }}
                  >
                    {t("Reminder For Calibration")}
                  </Table.HeaderCell>
                  {/* <Table.HeaderCell
                    sorted={
                      sort.name === "responsible_person"
                        ? ([undefined, "ascending", "descending"][
                            sort.dir
                          ] as any)
                        : undefined
                    }
                    onClick={() => {
                      if ("responsible_person" === sort.name) {
                        const dir = (sort.dir + 1) % 3;
                        setSort({ name: "responsible_person", dir });
                      } else {
                        setSort({ name: "responsible_person", dir: 1 });
                      }
                    }}
                  >
                    Responsible Person For Calibration
                  </Table.HeaderCell> */}
                </>}
                <th>
                  {currentUser.permissions.equipment.create && (
                    <Button
                      color="blue"
                      icon="add"
                      content={t('Inventory')}
                      size="mini"
                      onClick={(e) => {
                        setCurrentEditId(undefined);
                        editModalControl.setOpen(true);
                      }}
                    />
                  )}
                </th>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {equipments?.items.map((equipment) => {
                return (
                  <tr key={equipment.id + '_' + equipment.confirm_id} style={{ color: equipment.indent ? 'gray' : 'black' }}>
                    <td style={{ whiteSpace: 'nowrap' }}>{equipment.indent && !_.isEmpty(equipment.ancestor_path) ? equipment.ancestor_path?.join('.') : (equipment.id || 'New')}</td>
                    <td>{equipment.team}</td>
                    <td>{equipment.type}</td>
                    <td>{equipment.device}</td>
                    <td>{equipment.brand}</td>
                    <td>{equipment.model}</td>
                    <td>{equipment.part}</td>
                    <td>{equipment.number}</td>
                    <td>{equipment.engraved_number}</td>
                    <td>{equipment.po_number}</td>
                    <td>{equipment.location}</td>
                    <td>{equipment.storage_place}</td>
                    {searchData.by_bundles && <td>
                      {
                        equipment.type?.toLowerCase() == 'bundle' && config?.EQUIPMENT_BUNDLE_STATUS.find(
                          (e) => e[0] === equipment.bundle_status
                        )?.[1] || ''
                      }
                    </td>}
                    <td>
                      {
                        (config?.EQUIPMENT_CURRENT_STATES.find(
                          (e) => e[0] === equipment.current_state
                        ) || [null, ""])[1]
                      }
                    </td>
                    <td>{equipment.product.join(', ')}</td>
                    <td>{equipment.owner}</td>
                    <td>{equipment.lewitt_inventory_number}</td>
                    <td>{formatDateTime(equipment.last_modified_at)}</td>
                    {isNeedCalibration && <>
                      <td>{formatDate(equipment.calibration_date)}</td>
                      <td>{formatDate(equipment.next_calibration_date)}</td>
                      <td>{equipment.cause_of_no_calibration}</td>
                      <td>{equipment.responsible_person?.join(', ')}</td>
                      <td>{`before ${equipment.reminder || 0} days`}</td>
                      {/* <td>{equipment.responsible_person}</td> */}
                    </>}

                    <td>
                      <div style={{ display: 'flex', gap: '3px', flexShrink: 0, 'flexWrap': 'wrap' }}>
                        {equipment.id && currentUser.permissions.equipment.update &&
                          equipment.editable_for_current_user && (
                            <Button
                              onClick={(e) => {
                                setCurrentEditId(equipment.id);
                                editModalControl.setOpen(true);
                              }}
                              color="blue"
                              icon="edit"
                              size="mini"
                              title="Edit"
                            />
                          )}

                        {equipment.id && currentUser.permissions.equipment.delete && (
                          <Button
                            onClick={(e) => {
                              dialogControl.deletionConfirm(
                                "#" +
                                equipment.id +
                                " " +
                                equipment.type +
                                " " +
                                equipment.device +
                                " " +
                                equipment.brand +
                                " " +
                                equipment.model +
                                " " +
                                equipment.number,
                                (agree) => {
                                  if (agree) {
                                    return restRequest(
                                      `equipments/${equipment.id}`,
                                      null,
                                      "delete"
                                    ).then(() => {
                                      setSearchData((searchData: any) => ({
                                        ...searchData,
                                        refresh: (searchData.refresh || 1) + 1,
                                      }));
                                    });
                                  }

                                  return Promise.resolve();
                                }
                              );
                            }}
                            color="red"
                            icon="delete"
                            size="mini"
                            title="Delete"
                          />
                        )}
                        {equipment.id && equipment.need_calibration &&
                          currentUser.permissions.equipment.calibration.view && (
                            <Button
                              onClick={(e) => {
                                setCurrentCalibrationEquipmentId(equipment.id);
                                calibrationModalControl.setOpen(true);
                              }}
                              color="blue"
                              size="mini"
                              compact
                              title="Manage Calibrations"
                            >
                              {t('Calibration')}
                            </Button>
                          )}

                        {equipment.id && <Button
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentJournalizationId(equipment.id);
                            journalizationModalControl.setOpen(true);
                          }}
                          icon="history"
                          size="mini"
                          title="History"
                        ></Button>}

                        {currentUser.permissions.equipment.confirm &&
                          equipment.confirm_id && (
                            <Button
                              onClick={(e) => {
                                confirmModalControl.data = [
                                  equipment.confirm_id,
                                  confirmModalControl.data[1] + 1,
                                  equipment.id
                                ];
                                confirmModalControl.setOpen(true);
                              }}
                              color="blue"
                              size="mini"
                              compact
                            >
                              {t('Confirm')}
                            </Button>
                          )}

                        {currentUser.permissions.equipment.create && (
                          <Button
                            color="blue"
                            icon="copy"
                            size="mini"
                            onClick={(e) => {
                              setCurrentEditId(undefined);
                              setCurrentCopyEditId(equipment.id);
                              editModalControl.setOpen(true);
                            }}
                            title="Copy and New"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </Table.Body>
          </Table>
          <Page data={equipments} updatePage={setSearchData}></Page>
        </Segment>
      </Segment.Group>

      <Editor
        id={currentEditId}
        copyId={currentCopyEditId}
        config={config}
        modalControl={editModalControl}
        onFinish={(result) => {
          setCurrentCopyEditId(0);

          setSearchData((searchData: any) => ({
            ...searchData,
            refresh: (searchData.refresh || 1) + 1,
          }));
          editModalControl.setOpen(false);
        }}
      ></Editor>

      {config && confirmModalControl.data[0] > 0 && (
        <ConfirmEditor
          config={config}
          modalControl={confirmModalControl}
          onFinish={(result) => {
            setSearchData((searchData: any) => ({
              ...searchData,
              refresh: (searchData.refresh || 1) + 1,
            }));
            confirmModalControl.setOpen(false);
          }}
        ></ConfirmEditor>
      )}

      {currentCalibrationEquipmentId && (
        <EquipmentCalibrationComponent
          equipmentId={currentCalibrationEquipmentId}
          modalControl={calibrationModalControl}
        />
      )}

      {currentJournalizationId && config && (
        <Journalization
          id={currentJournalizationId}
          modalControl={journalizationModalControl}
          config={config}
        />
      )}

      {printQrModalControl.data && (
        <EquipmentComponentPrintQR modalControl={printQrModalControl} />
      )}

      <ImportEquipment
        modalControl={importModalControl}
        done={() => {
          setSearchData((searchData: any) => ({
            ...searchData,
            refresh: (searchData.refresh || 1) + 1,
          }));
          importModalControl.setOpen(false);
        }}
      />
    </>
  );
}

function Editor(props: {
  id?: number;
  copyId?: number;
  modalControl: ModalControlData;
  config?: EquipmentConfig;
  onFinish: (data: EquipmentData) => void;
}) {
  const { id, copyId, modalControl, config, onFinish } = props;
  const isCreate = !id;

  const [data, setField, setData] = useFormData<EquipmentData>({bundle_status: 1} as any);
  const [invalidError, setInvalidError] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [t] = useContext(LanguageContext);
  const currentUser = useCurrentUser();


  const isBundle = (data.type?.toLowerCase().trim() === 'bundle');
  const isUsed = (data.current_state === 1);

  useEffect(() => {
    if (!modalControl.props.open) {
      return;
    }

    setData({ need_calibration: false, bundle_status: 1 } as any);
    setInvalidError({});
    if (id) {
      setLoading(true);
      restRequest<EquipmentData>(`equipments/${id}/for_edit`)
        .then(setData)
        .finally(() => setLoading(false));
    } else if (copyId) {
      setLoading(true);
      restRequest<EquipmentData>(`equipments/${copyId}/for_copy`)
        .then(setData)
        .finally(() => setLoading(false));
    }
  }, [id, copyId, modalControl.props.open]);

  const onSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    formProps: FormProps
  ) => {
    event.preventDefault();

    setLoading(true);

    restRequest<EquipmentData>(
      `equipments/${isCreate ? "" : id}`,
      { ...data, parent_id: data.parent_id || 0 },
      isCreate ? "post" : "put"
    )
      .then(
        (result) => {
          props.onFinish(result);
          setInvalidError({});

          notificationControl.showSuccess(`${isCreate ? "Created" : "Updated"} successfully`);
        },
        (resp) => {
          if (resp.status === 2) {
            setInvalidError(resp.data);
          }
        }
      )
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Modal {...props.modalControl.props} size="large">
        <Modal.Header>
          {t(isCreate ? ["Create", " ", "Inventory"] : ["Edit", "", "Inventory", ` #${id}`], [3])}
        </Modal.Header>
        <Modal.Content>
          <Form onSubmit={onSubmit} loading={loading}>
            <Form.Group widths="equal">
              <Form.Input
                label={t("Parent")}
                error={fieldError("parent_id", invalidError)}
              >
                <LewittRemoteSearchDropdown name="parent_id" value={data.parent_id} onChange={setField} url="equipments/parent_dropdown_options" />
              </Form.Input>
              <CommonEnumSelector
                typeId={ENUM_TYPES_EQUIPMENT_TEAM}
                label={t("Team")}
                name="team"
                value={data.team || ""}
                onChange={setField}
                required
                error={fieldError("team", invalidError)}
              />
              <CommonEnumSelector
                typeId={ENUM_TYPES_EQUIPMENT_TYPE}
                label={t("Type")}
                name="type"
                value={data.type || ""}
                onChange={setField}
                required
                error={fieldError("type", invalidError)}
              />
              <CommonEnumSelector
                typeId={ENUM_TYPES_EQUIPMENT_DEVICE}
                label={t("Product Category")}
                name="device"
                value={data.device || ""}
                onChange={setField}
                required
                error={fieldError("device", invalidError)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <CommonEnumSelector
                typeId={ENUM_TYPES_EQUIPMENT_BRAND}
                label={t("Brand")}
                name="brand"
                value={data.brand || ""}
                onChange={setField}
                required={!isBundle}
                error={fieldError("brand", invalidError)}
              />

              <CommonEnumSelector
                typeId={ENUM_TYPES_EQUIPMENT_MODEL}
                label={t("Model")}
                name="model"
                value={data.model || ""}
                onChange={setField}
                required
                error={fieldError("model", invalidError)}
              />

              <CommonEnumSelector
                typeId={ENUM_TYPES_EQUIPMENT_PART}
                label={t("Part")}
                name="part"
                value={data.part || ""}
                onChange={setField}
                error={fieldError("part", invalidError)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label={t("Serial Number")}
                name="number"
                value={data.number || ""}
                onBlur={trimSetField(setField)}
                onChange={setField}
                error={fieldError("number", invalidError)}
              ></Form.Input>

              <Form.Input
                label={t("Engraved Serial")}
                name="engraved_number"
                value={data.engraved_number || ""}
                onBlur={trimSetField(setField)}
                onChange={setField}
                error={fieldError("engraved_number", invalidError)}
              ></Form.Input>

              <Form.Input
                label={t("PO Number")}
                name="po_number"
                value={data.po_number || ""}
                onBlur={trimSetField(setField)}
                onChange={setField}
                error={fieldError("po_number", invalidError)}
              ></Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              {data.type?.toLowerCase() === 'bundle' && <>
                <Form.Input
                  label={t("Bundle Status")}
                >
                  {config?.EQUIPMENT_BUNDLE_STATUS.find(s => data?.bundle_status === s[0])?.[1] || ''}
                </Form.Input>
              </>}
              <Form.Input
                label={t("Current State")}
                required
                error={fieldError("current_state", invalidError)}
              >
                <Dropdown
                  name="current_state"
                  value={data.current_state || false}
                  onChange={setField}
                  options={(
                    (config && config.EQUIPMENT_CURRENT_STATES) ||
                    []
                  ).map((v) => ({ key: v[0], text: v[1], value: v[0] }))}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>
              <Form.Input
                label={t("Lewitt Inventory Number")}
                name="lewitt_inventory_number"
                value={data.lewitt_inventory_number || ""}
                onChange={setField}
                required={false}
                error={fieldError("lewitt_inventory_number", invalidError)}
              ></Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <CommonEnumSelector
                typeId={ENUM_TYPES_EQUIPMENT_PROJECT_USED_FOR}
                label={t("Project Used For")}
                name="product"
                value={data.product || []}
                onChange={setField}
                required
                multiple
                error={fieldError("product", invalidError)}
              />

              <CommonEnumSelector
                typeId={ENUM_TYPES_EQUIPMENT_LOCATION}
                label={t("Location")}
                name="location"
                value={data.location || ""}
                onChange={setField}
                required={data.current_state == 1}
                error={fieldError("location", invalidError)}
              />

              <CommonEnumSelector
                typeId={ENUM_TYPES_EQUIPMENT_STORAGE_PLACE}
                label={t("Storage Place")}
                name="storage_place"
                value={data.storage_place || ""}
                onChange={setField}
                required={data.current_state == 2}
                error={fieldError("storage_place", invalidError)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label={t("Owner")}
                name="owner"
                value={data.owner || ""}
                onChange={setField} onBlur={trimSetField(setField)}
                required={isUsed}
                error={fieldError("owner", invalidError)}
              ></Form.Input>
              <Form.Input
                label={t("Budget Code")}
                name="budget_code"
                value={data.budget_code || ""}
                onChange={setField} onBlur={trimSetField(setField)}
                required={false}
                error={fieldError("budget_code", invalidError)}
              ></Form.Input>
              <Form.Input
                label={t("Give back Time")}
                required={false}
                error={fieldError("back_time", invalidError)}
              >
                <ReactDatePicker
                  showTimeSelect
                  selected={data.back_time ? new Date(data.back_time) : null}
                  dateFormat="yyyy-MM-d HH:mm"
                  onChange={(d: Date) =>
                    setField(null, {
                      name: "back_time",
                      value: d && d.toISOString(),
                    })
                  }
                  timeFormat="HH:mm"
                ></ReactDatePicker>
              </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label={t("Need Calibration")}
                required
                error={fieldError("need_calibration", invalidError)}
              >
                <Dropdown
                  name="need_calibration"
                  value={data.need_calibration}
                  onChange={setField}
                  options={[
                    { key: 1, text: "Yes", value: true },
                    { key: 2, text: "No", value: false },
                  ]}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>

              <Form.Input required={data.need_calibration} label={t("Responsible Person For Calibration")} error={fieldError('responsible_person', invalidError)}>
                <LewittRemoteSearchDropdown allowAdditions fluid selection multiple name="responsible_person" value={data.responsible_person || ''} onChange={setField} url="users/email_dropdown_options" />
              </Form.Input>

              <Form.Input required={data.need_calibration} label={t("Reminder")} error={fieldError('reminder', invalidError)}>
                <Input labelPosition="right"  >
                  <Label>{t("before")}</Label>
                  <input name="reminder" type="number" step={1} value={data.reminder || 0} onChange={e => setField(null, { name: 'reminder', value: e.target.value })} />
                  <Label>{t("days")}</Label>
                </Input>
              </Form.Input>
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Input label={t("Attachment")}>
                <FilesInput name="files" value={data.files || []} onChange={setField} containerType={FileContainerType.EquipmentFile} containerId={id || 0}></FilesInput>
              </Form.Input>
            </Form.Group>

            {!data?.id && <Form.Group widths="equal">
              <Form.Input label={t("Comment")} error={fieldError('remark', invalidError)}>
                <TextEditor name="remark" value={data.remark || ''} onChange={setField} />
              </Form.Input>
            </Form.Group>}
            <div>
              <Button primary>{data.id ? t("Update") : t("New")}</Button>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {data?.id && data.bundle_status === 1 && currentUser.permissions.equipment.bundle_lock && <>
                <Button as="a" primary onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();

                  dialogControl.confirm("Are you sure to lock the whole bundle?", agree => {
                    if (agree) {
                      return restRequest<number>(`equipments/${id}/bundle_lock`, {}, 'post').then(newId => {
                        setData(d => ({...d, bundle_status: 2}));
                        props.onFinish(data);
                        notificationControl.showSuccess('done', null, true, 1);
                      }).catch(e => {})
                    }

                    return Promise.resolve();
                  });
                }}>Lock Bundle</Button>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </>}
              {data?.id && data.bundle_status === 2 && currentUser.permissions.equipment.bundle_unlock && <>
                <Button as="a" primary onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();

                  dialogControl.confirm("Are you sure to unlock the whole bundle?", agree => {
                    if (agree) {
                      return restRequest<number>(`equipments/${id}/bundle_unlock`, {}, 'post').then(newId => {
                        setData(d => ({...d, bundle_status: 1}));
                        props.onFinish(data);
                        notificationControl.showSuccess('done', null, true, 1);
                      }).catch(e => {})
                    }

                    return Promise.resolve();
                  });
                }}>Unlock Bundle</Button>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </>}
              {data?.id && data.bundle_status === 2 && currentUser.permissions.equipment.bundle_retire && <>
                <Button as="a" primary onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();

                  dialogControl.confirm("Are you sure to retire the whole bundle?", agree => {
                    if (agree) {
                      return restRequest<number>(`equipments/${id}/bundle_retire`, {}, 'post').then(newId => {
                        setData(d => ({...d, bundle_status: 3}));
                        props.onFinish(data);
                        notificationControl.showSuccess('done', null, true, 1);
                      }).catch(e => {})
                    }

                    return Promise.resolve();
                  });
                }}>Retire Bundle</Button>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <Button as="a" primary onClick={e => {
                  e.stopPropagation();
                  e.preventDefault();

                  dialogControl.confirm("Are you sure to copy and retire the whole bundle?", agree => {
                    if (agree) {
                      return restRequest<number>(`equipments/${id}/bundle_copy_and_retire`, {}, 'post').then(newId => {
                        setData(d => ({...d, bundle_status: 3}));
                        props.onFinish(data);
                        dialogControl.alert('The copied bundle ID is ' + newId);
                      }).catch(e => {})
                    }

                    return Promise.resolve();
                  });
                }}>Copy and Retire Bundle</Button>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </>}
              <Button as="a" onClick={(e) => props.modalControl.setOpen(false)}>
                {t("Close")}
              </Button>
            </div>
          </Form>

          {data.id && <>
            <Divider />
            <Comments containerType={2} containerId={data.id} />
          </>}
        </Modal.Content>
      </Modal>
    </>
  );
}

function ConfirmEditor(props: {
  modalControl: ModalControlData<[number, number, number]>;
  config: EquipmentConfig;
  onFinish: (data: EquipmentData) => void;
}) {
  const { modalControl, config, onFinish } = props;
  const [change_id, refresh_number, equipment_id] = modalControl.data;

  const [notConfirmChange, isLoadingNotConfirmChange] = useRequest<{
    [fieldName: string]: any;
  }>(
    "equipments/not_confirmed_change/" +
    change_id +
    "?refresh=" +
    refresh_number,
    null,
    "get"
  );
  const [data, setField, setData] = useFormData<EquipmentChangeData>({
    state: 2,
  } as any);
  const [invalidError, setInvalidError] = useState<any>({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [t, lang] = useContext(LanguageContext);

  const onSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    formProps: FormProps
  ) => {
    event.preventDefault();

    setIsSubmitLoading(true);

    restRequest<EquipmentData>(`equipments/confirm/${change_id}`, data, "post")
      .then(
        (result) => {
          props.onFinish(result);
          setData({ state: 2 } as any);
          setInvalidError({});
        },
        (resp) => {
          if (resp.status === 2) {
            setInvalidError(resp.data);
          }
        }
      )
      .finally(() => setIsSubmitLoading(false));
  };

  return (
    <>
      <Modal {...props.modalControl.props} size="large">
        <Modal.Header>{equipment_id > 0 ? `Confirm Change for ` + `Equipment #${equipment_id}` : 'Confirm New Equipment'}</Modal.Header>
        <Modal.Content>
          <Segment size="mini" loading={isLoadingNotConfirmChange}>
            <Header>{notConfirmChange?.created_by_name} <Header.Subheader>{formatDateTime(notConfirmChange?.created_at)}</Header.Subheader></Header>

            {notConfirmChange && (
              <JournalizationDetailContent
                detailFields={detailFields(config, t, lang)}
                details={notConfirmChange}
              ></JournalizationDetailContent>
            )}
          </Segment>
          <Form onSubmit={onSubmit} loading={isSubmitLoading}>
            <Form.Group widths="equal">
              <Form.Input
                label={t("Result")}
                required
                error={fieldError("state", invalidError)}
              >
                <Dropdown
                  name="state"
                  value={data.state}
                  onChange={setField}
                  options={(
                    (config && config.EQUIPMENT_CHANGE_STATES) ||
                    []
                  ).map((v) => ({ key: v[0], text: v[1], value: v[0] }))}
                  search
                  selection
                  fluid
                ></Dropdown>
              </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Input
                label={t("Note")}
                value={data.confirm_note}
                error={fieldError("confirm_note", invalidError)}
              >
                <TextEditor
                  value={data.confirm_note || ""}
                  name="confirm_note"
                  onChange={setField}
                />
              </Form.Input>
            </Form.Group>
            <div>
              <Button primary>{t("Submit")}</Button>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              <Button as="a" onClick={(e) => props.modalControl.setOpen(false)}>
                {t("Cancel")}
              </Button>
            </div>
          </Form>
        </Modal.Content>
      </Modal>
    </>
  );
}

const FIELD_NAMES = {
  type: "Type",
  device: "Product Category",
  brand: "Brand",
  model: "Model",
  part: "Part",
  number: "Serial Number",
  engraved_number: "Engraved Serial",
  bundle_status: "Bundle Status",
  current_state: "Current State",
  product: "Project Used For",
  storage_place: "Storage Place",
  back_time: "Give back Time",
  need_calibration: "Need Calibration",
  calibrations: "Calibrations",
  calibration_date: "Calibration Date",
  next_calibration_date: "Next Calibration",
  cause_of_no_calibration: "Reason For Missing Calibration",
  calibration_files: "Attachment",
  responsible_person: "Responsible Person For Calibration",
  reminder: "Reminder For Calibration",
  budget_code: "Budget Code",
  lewitt_inventory_number: "Lewitt Inventory Number",
  owner: "Owner",
  files: "Attachment",
  team: "Team",
  location: "Location",
  parent_id: "Parent",
  po_number: 'PO Number'
};

const detailFields: (config: EquipmentConfig, t: Translate, lang: LANG) => JournalizationDetailField[] = (
  config, t, lang
) => {
  return [
    {
      key: "parent_id", name: t(FIELD_NAMES.parent_id),
      formater: (v) => v ? restRequest<string>('equipments/equipment_name/' + v, null, 'get') : 'None'
    },
    {
      key: "team", name: t(FIELD_NAMES.team), formater: (v) => formatEnumName(ENUM_TYPES_EQUIPMENT_TEAM, v, lang),
    },
    { key: "type", name: t(FIELD_NAMES.type), formater: (v) => formatEnumName(ENUM_TYPES_EQUIPMENT_TYPE, v, lang) },
    { key: "device", name: t(FIELD_NAMES.device), formater: (v) => formatEnumName(ENUM_TYPES_EQUIPMENT_DEVICE, v, lang) },
    { key: "brand", name: t(FIELD_NAMES.brand), formater: (v) => formatEnumName(ENUM_TYPES_EQUIPMENT_BRAND, v, lang) },
    { key: "model", name: t(FIELD_NAMES.model), formater: (v) => formatEnumName(ENUM_TYPES_EQUIPMENT_MODEL, v, lang) },
    { key: "part", name: t(FIELD_NAMES.part), formater: (v) => formatEnumName(ENUM_TYPES_EQUIPMENT_PART, v, lang) },
    { key: "number", name: t(FIELD_NAMES.number) },
    { key: "engraved_number", name: t(FIELD_NAMES.engraved_number) },
    { key: "po_number", name: t(FIELD_NAMES.po_number) },
    {
      key: "bundle_status",
      name: t(FIELD_NAMES.bundle_status),
      formater: (v) => {
        const obj = config.EQUIPMENT_BUNDLE_STATUS.find((e) => e[0] === v);
        if (obj) {
          return obj[1];
        }

        return "";
      },
    },
    {
      key: "current_state",
      name: t(FIELD_NAMES.current_state),
      formater: (v) => {
        const obj = config.EQUIPMENT_CURRENT_STATES.find((e) => e[0] === v);
        if (obj) {
          return obj[1];
        }

        return "";
      },
    },
    { key: "product", name: t(FIELD_NAMES.product), isList: (jd) => _.isArray(jd) && (_.isArray(jd[0]) || _.isArray(jd[1])), formater: (v) => formatEnumName(ENUM_TYPES_EQUIPMENT_PROJECT_USED_FOR, v, lang) },
    { key: "owner", name: t(FIELD_NAMES.owner) },
    { key: "budget_code", name: t(FIELD_NAMES.budget_code) },
    { key: "location", name: t(FIELD_NAMES.location), formater: (v) => formatEnumName(ENUM_TYPES_EQUIPMENT_LOCATION, v, lang) },
    { key: "storage_place", name: t(FIELD_NAMES.storage_place), formater: (v) => formatEnumName(ENUM_TYPES_EQUIPMENT_STORAGE_PLACE, v, lang) },
    { key: "back_time", name: t(FIELD_NAMES.back_time), formater: formatDateTime },
    {
      key: "need_calibration",
      name: t(FIELD_NAMES.need_calibration),
      formater: (v) => (v ? "Yes" : "No"),
    },
    { key: "lewitt_inventory_number", name: t(FIELD_NAMES.lewitt_inventory_number) },
    {
      key: 'responsible_person',
      name: t(FIELD_NAMES.responsible_person),
      isList: true
    },
    {
      key: 'reminder',
      name: t(FIELD_NAMES.reminder),
      formater: (v) => v ? (`before ${v} days`) : ''
    },
    {
      key: "calibrations",
      name: t(FIELD_NAMES.calibrations),
      isList: true,
      formater: (v) => "#" + v["id"],
      childrenKeyFormatter: (v) => "#" + v,
      children: [
        {
          key: "calibration_date",
          name: t(FIELD_NAMES.calibration_date),
          formater: formatDateTime,
        },
        {
          key: "next_calibration_date",
          name: t(FIELD_NAMES.next_calibration_date),
          formater: formatDateTime,
        },
        {
          key: "cause_of_no_calibration",
          name: t(FIELD_NAMES.cause_of_no_calibration),
        },
        {
          key: "files",
          name: t(FIELD_NAMES.calibration_files),
          isList: true,
          formater: (v) => v["name"],
        }
      ],
    },
    {
      key: "files",
      name: t(FIELD_NAMES.files),
      isList: true,
      formater: (v) => v["name"],
    }
  ]
};

function Journalization(props: {
  id: number;
  modalControl: ModalControlData;
  config: EquipmentConfig;
}) {
  const { id, modalControl, config } = props;
  const [t, lang] = useContext(LanguageContext);

  return (
    <Modal {...modalControl.props} size="large">
      <Modal.Header>{t("Journalization")} #{id}</Modal.Header>
      <Modal.Content>
        <JournalizationComponent
          containerId={props.id}
          containerType={4}
          detailFields={detailFields(config, t, lang)}
          renderAddition={(changedData) => {
            if (changedData.confirmed_by) {
              return (
                <Message
                  positive={changedData.confirm_agree}
                  negative={!changedData.confirm_agree}
                >
                  <Message.Header>
                    {lang === 'en' ? <>
                      {changedData.confirm_agree ? "Agreed" : "Disagreed"} by{" "}
                      {changedData.confirmed_by}
                    </> : <>{changedData.confirmed_by}已{changedData.confirm_agree ? "同意" : "拒绝"}</>}

                  </Message.Header>
                  {changedData.confirm_note && (
                    <p
                      dangerouslySetInnerHTML={{
                        __html: changedData.confirm_note,
                      }}
                    ></p>
                  )}
                </Message>
              );
            } else if (changedData.to_be_confirmed) {
              return (
                <Message>
                  <p>{t("To Be Confirmed")}</p>
                </Message>
              );
            } else if (changedData.bundle_copy_from_id) {
              return (<Message size="tiny" warning>Copied from #{changedData.bundle_copy_from_id} when copied and retired the whole bundle</Message>)
            } else if (changedData.bundle_copy_to_id) {
              return (<Message size="tiny" warning>The whole bundle had copied to #{changedData.bundle_copy_to_id}</Message>)
            }
          }}
          customClassName={(changedData) => {
            if (changedData.to_be_confirmed) {
              return "disabled";
            }

            return "";
          }}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          as="a"
          onClick={(e) => {
            e.preventDefault();

            modalControl.setOpen(false);
          }}
        >
          {t("Close")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

function ImportEquipment(props: {
  modalControl: ModalControlData<EquipmentData>;
  done: () => void;
}) {
  const { modalControl, done } = props;
  const [file, setFile] = useState<any>();
  const [loading, setLoading] = useState(false);

  const onSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    formProps: FormProps
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setLoading(true);

    const formData = new FormData();

    if (file) {
      formData.append("excel", file);
    }

    httpRequest<any>(`equipments/import_from_excel`, formData, "post", {})
      .then((result) => {
        done();
        notificationControl.showSuccess("Import excel successfully");
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal {...modalControl.props} size="large" closeIcon>
      <Modal.Header>Import Equipments</Modal.Header>
      <Modal.Content>
        <Form onSubmit={onSubmit} loading={loading}>
          <Form.Group widths="equal">
            <Form.Input label="Excel" required>
              <input
                type="file"
                name="csv_file"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    setFile(e.target.files[0]);
                  }
                }}
              />
            </Form.Input>
          </Form.Group>
          <div>
            <Button primary>Submit</Button>
          </div>
        </Form>
      </Modal.Content>
    </Modal>
  );
}
